import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

import Header from '../components/header';
import Footer from '../components/Footer';
import InfoBar from '../components/InfoBar';
import { ExpandableContextTable } from '../components/ExpandableContextTable';
import api from '../util/api';
import * as auth from '../util/auth';

const COLUMNS = [
    { id: 'shortName', Header: 'Type', accessorKey: 'shortName' },
    {
        id: 'price',
        Header: 'Price',
        accessorKey: 'price',
        renderFunc: (price) => `${price.toFixed(2)} €`,
    },
    { id: 'source', Header: 'Source', accessorKey: 'source' },
    {
        id: 'checkedOn',
        Header: 'Last checked',
        accessorKey: 'checkedOn',
        renderFunc: (date) => date ? date.toLocaleDateString() : '-',
    },
];

function PriceTableRow({ row, columns }) {
    return (
        <tr key={row.id}>
            {columns.map((column) => (
                <td key={column.id + '.' + row.id}>
                    {column.renderFunc ? column.renderFunc(row[column.accessorKey]) : row[column.accessorKey]}
                </td>
            ))}
        </tr>
    );
}

export default function PriceUpdate() {
    const [prices, setPrices] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        auth.hasRoleTubeReader().then((hasRole) => {
            if (!hasRole) {
                setError("You do not have permission to view this page.");
                return;
            }
            api.getPrices()
                .then(setPrices)
                .catch((err) => setError(`Failed to fetch prices: ${err}`));
        });
    }, []);

    /**
     * Fetches the latest prices for all tube types from the respective shops and 
     * updates them in the backend and frontend state.
     * Currently, only Draeger is supported as they are the producers of our tubes. 
     * Could be extended to support other shops as well.
     */
    async function fetchUpdatedPrices() {
        const tubeTypes = await api.getTubeTypes();
        const parser = new DOMParser();

        for (const tubeType of tubeTypes) {
            if (!tubeType.orderCode) continue;

            // Fetch the product page and extract price from meta tag
            // Currently, only Draeger is supported as they are the producers of our tubes.
            // Could be extended to support other shops as well.
            const url = `https://shop.draeger.com/search?search=${encodeURIComponent(tubeType.orderCode)}`;
            let response;
            try {
                response = await fetch(url);
            } catch {
                continue;
            }
            if (!response.ok) continue;

            const doc = parser.parseFromString(await response.text(), 'text/html');
            const meta = doc.querySelector('meta[property="product:price:amount"]');
            if (!meta) continue;

            const price = parseFloat(meta.getAttribute('content'));
            if (isNaN(price)) continue;

            const checkedOn = new Date();
            const sourceRegex = /https?:\/\/([^/]+)/;
            const source = url.match(sourceRegex)[1];

            // Update price in backend
            try {
                await api.postPrice(tubeType.shortName, price, source, checkedOn);
            } catch {
                continue;
            }

            // Update the price in the frontend state
            const updated = {
                id: tubeType.shortName,
                shortName: tubeType.shortName,
                price,
                source,
                checkedOn,
            };

            setPrices(prev => {
                const exists = prev.some(p => p.shortName === tubeType.shortName);
                return exists
                    ? prev.map(p => p.shortName === tubeType.shortName ? updated : p)
                    : [...prev, updated];
            });
        }
    }

    function rowFactory({ row, columns, onRowClicked }) {
        return <PriceTableRow key={row.id} row={row} columns={columns} onRowClicked={onRowClicked} />;
    }

    return (
        <div className="price-update">
            <Header />
            <div className="content">
                <InfoBar show={error !== ''} text={error} title="Error" color="#F00" />
                <div className="d-flex justify-content-center mb-3">
                    <Button
                        size='sm'
                        variant="outline-secondary"
                        onClick={fetchUpdatedPrices}>
                        Preise aktualisieren
                    </Button>
                </div>
                <ExpandableContextTable
                    columns={COLUMNS}
                    items={prices}
                    contextRowFactory={rowFactory}
                />
            </div>
            <Footer />
        </div>
    );
}
